import React, { useMemo, useState } from 'react';
import { Card, Row, Col, Divider, Descriptions, Typography, Tabs } from "antd";
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import NivoBarChart from "../../charts/nivo/NivoBarChart";

import SectorWaterfall from "./Sectors/SectorWaterfall";
import SectorScatter from "./Sectors/SectorScatter";
import SectorDeviationTable from "./Sectors/SectorDeviationTable";
import { computeSectorDeviations, countTickersBySector } from "./utils/smaUtils";
import Logo from "../../components/Graphics/Logo";
import { shortenSector } from "../../utils/abbreviations";
import Grouped3DBarChart from "../../charts/Anychart/Grouped3dBar";
import {useScreenWidth} from "../../hooks/useScreen";
import {stringMixin} from "../../utils/strings";
const convertForWaterfall = (o) => {
    const arr = [];
    for (const key in o) {
        arr.push({ x: shortenSector(key), y: o[key] })
    }
    arr.sort((a, b) => b.y - a.y);
    return arr;
};


const SectorCountBlock = ({ counts, threshold=1 }) => {
    const filteredKeys = Object.keys(counts).filter(x=>counts[x]>=threshold);
    const borderBottom = filteredKeys.length > 12 ? '2px dashed #ededed' : 'none'; // No border if less than 12 sectors
    return (
        <Descriptions column={2} size={'small'} bordered style={{ height: '200px', overflowY: 'auto', borderBottom}} className={'hide-scrollbar'}>
            {filteredKeys.map(sector => (
                <Descriptions.Item style={{textTransform:'capitalize'}} key={sector} label={shortenSector(sector)}>{counts[sector]}</Descriptions.Item>
            ))}
        </Descriptions>
    );
};

const SectorWeightBlock = ({ weights, threshold=0.01 }) => {
    const filteredKeys = Object.keys(weights).filter(x=>weights[x]>=threshold)
    const borderBottom = filteredKeys.length > 12 ? '2px dashed #ededed' : 'none'; // No border if less than 12 sectors

    return (
        <Descriptions column={2} size={'small'} bordered style={{ height: '200px', overflowY:'auto', borderBottom }} className={'hide-scrollbar'}>
            {filteredKeys.map(sector => (
                <Descriptions.Item style={{textTransform:'capitalize'}} key={sector} label={shortenSector(sector)}>{stringMixin.pct(weights[sector])}</Descriptions.Item>
            ))}
        </Descriptions>
    );
};

const PortfolioColumn = ({ logoSrc, title, counts, weights, tickerData, waterfallBar }) => {
    return (
        <div className={' p-4 round-1'}>
            <Row align={'middle'} justify={'start'} gutter={16} className={'mb-5'}>
                <Logo src={logoSrc} height={'35px'} width={'35px'} />
                <Typography.Title level={3}>{title}</Typography.Title>
            </Row>
            <Divider />
            <SectorCountBlock counts={counts} />
            <Divider />
            <div style={{ height: 400, width: 600 }}>
                <SectorScatter tickerData={tickerData} />
            </div>
            <Divider />
            <SectorWeightBlock weights={weights} />
            <Divider />
            <div style={{ height: 400, width: 600 }}>
                <SectorWaterfall sectors={waterfallBar} />
            </div>
        </div>
    );
};

const SectorsComparison = ({ summaryDetails, cutTickerData, fullTickerData }) => {
    const waterfallBar = useMemo(() => convertForWaterfall(summaryDetails.sectorDistribution), [summaryDetails.sectorDistribution]);
    const originalWaterfallBar = useMemo(() => convertForWaterfall(summaryDetails.originalSectorDistribution), [summaryDetails.originalSectorDistribution]);

    const countsPerSectorKept = useMemo(() => countTickersBySector(cutTickerData), [cutTickerData]);
    const countsPerSectorOriginal = useMemo(() => countTickersBySector(fullTickerData), [fullTickerData]);

    return (
        <Row justify={'center'} align={'top'} gutter={[64, 64]}>
            <Col xs={24} xl={12}>
                <PortfolioColumn
                    logoSrc={'https://storage.googleapis.com/finiat-web-static-resources/utility/scissors-icon.png'}
                    title={'Condensed Portfolio'}
                    counts={countsPerSectorKept}
                    weights={summaryDetails.sectorDistribution}
                    tickerData={cutTickerData}
                    waterfallBar={waterfallBar}
                />
            </Col>
            <Col xs={24} xl={12}>
                <PortfolioColumn
                    logoSrc={'https://storage.googleapis.com/finiat-web-static-resources/utility/layers-icon.png'}
                    title={'Full Portfolio (Original)'}
                    counts={countsPerSectorOriginal}
                    weights={summaryDetails.originalSectorDistribution}
                    tickerData={fullTickerData}
                    waterfallBar={originalWaterfallBar}
                />
            </Col>
        </Row>
    );
};


const SmaCharts = ({ sectorsBarChartField, setSectorsBarChartField, sectorsBarData, summaryDetails,
    cutTickerData, fullTickerData, key }) => {

    const sectorDeviations = useMemo(() => {
        return computeSectorDeviations(summaryDetails.sectorDistribution, summaryDetails.originalSectorDistribution)
    }, [summaryDetails.sectorDistribution, summaryDetails.originalSectorDistribution]);
    const [barChartType, setBarChartType] = useState('2d');
    console.log(summaryDetails, cutTickerData, fullTickerData);
    const screenWidth = useScreenWidth();

    return (
        <div>
            <Tabs defaultActiveKey="overview"
                  centered={true}
                  tabBarExtraContent={{
                      left: (
                          <ButtonGroup>
                            <Button variant={sectorsBarChartField === 'wgt' ? 'contained' : 'outlined'} onClick={() => setSectorsBarChartField('wgt')}>Weight</Button>
                            <Button variant={sectorsBarChartField === 'count' ? 'contained' : 'outlined'} onClick={() => setSectorsBarChartField('count')}>Count</Button>
                          </ButtonGroup>
                      )
                  }}
            >
                <Tabs.TabPane tab="Overview" key="overview">
                    <Row gutter={64} align={'top'} justify={'center'} wrap={screenWidth < 1400}>
                        <Col flex={'0 0 800px'}>
                            <Card bordered={false} className={'gapless'}>
                                <div style={{ width: '100%', height: '80vh' }}>
                                    {barChartType === '3d' && (
                                        <Grouped3DBarChart data={sectorsBarData}
                                            fieldNames={['Reduced Stocks', 'Full Portfolio']}
                                            height={750}
                                            colors={{
                                                'Full Portfolio': 'var(--primary)',
                                                'Reduced Stocks': '#0083ff'
                                            }}
                                            key={key}
                                        />
                                    )}
                                    {barChartType === '2d' && (
                                        <NivoBarChart
                                            data={sectorsBarData}
                                            keys={['Full Portfolio', 'Reduced Stocks']}
                                            customColors={{
                                                'Full Portfolio': 'var(--primary)',
                                                'Reduced Stocks': '#0083ff'
                                            }}
                                            leftAxisLabel={''}
                                            bottomAxisLabel={sectorsBarChartField === 'wgt' ? 'Weight (%)' : 'Number of Tickers'}
                                            rotateLabels={0}
                                            paddingBottom={30}
                                            paddingLeft={120}
                                            vertical={false}
                                            barPadding={0.1}
                                            gradientStep={10}
                                            postfix={sectorsBarChartField === 'wgt' ? '%' : ''}
                                        />
                                    )}
                                </div>
                            </Card>
                        </Col>
                        <Col flex={'0 0 600px'} className={'py-6'}>
                            <SectorDeviationTable deviations={sectorDeviations} extra={null} />
                        </Col>
                    </Row>
                </Tabs.TabPane>

                <Tabs.TabPane tab="Comparison" key="comparison">
                    <SectorsComparison summaryDetails={summaryDetails} cutTickerData={cutTickerData} fullTickerData={fullTickerData} />
                </Tabs.TabPane>
            </Tabs>
        </div>
    );
}

export default SmaCharts;
